import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { XIcon, FileIcon } from '../ui/Icons';
import { normalizeFileName } from '../ui/utils/fileNameEncoding';
import SplitViewFieldsGrid from './SplitViewFieldsGrid';

/**
 * SplitViewPanel - Right-side split view panel for showing record details
 */
const SplitViewPanel = ({
  isOpen,
  selectedRecord,
  selectedRecordFiles,
  config,
  fields,
  labels,
  onClose,
}) => {
  const { theme } = useTheme();

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn('hidden lg:block min-h-0 h-full pb-2')}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <div
        className={cn(
          'min-h-0 h-full rounded-xl border flex flex-col',
          theme.border.light,
          theme.colors.surface,
          theme.shadow.md
        )}
      >
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className={cn('text-sm font-semibold', theme.text.primary)}>
            {selectedRecord
              ? `${labels.viewTitle}`
              : labels.noSelectionText || 'Select a row to view details'}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cn('p-1 rounded hover:opacity-80', theme.text.secondary)}
            title={labels.closeText || 'Close'}
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        <div
          className="p-4 flex-1 overflow-auto advanced-table-scroll"
          style={{ scrollbarWidth: 'thin' }}
        >
          {selectedRecord ? (
            <div className="grid grid-cols-1 gap-4">
              <SplitViewFieldsGrid
                selectedRecord={selectedRecord}
                fields={fields}
                config={config}
                layout="boxed"
              />

              {config.features?.fileAttachments &&
                selectedRecordFiles &&
                selectedRecordFiles.filter((file) => !file.isDeleted).length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FileIcon className="w-5 h-5 text-blue-500" />
                      <div className={cn('text-sm font-semibold', theme.text.primary)}>
                        File Attachments (
                        {selectedRecordFiles.filter((file) => !file.isDeleted).length})
                      </div>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedRecordFiles
                        .filter((file) => !file.isDeleted)
                        .map((file, index) => (
                          <div
                            key={index}
                            className={cn(
                              'flex items-center gap-3 p-3 rounded-lg border',
                              theme.colors.background,
                              theme.border.subtle
                            )}
                          >
                            <FileIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div
                                className={cn('text-sm font-medium truncate', theme.text.primary)}
                              >
                                {normalizeFileName(file.fileName || '')}
                              </div>
                              <div className={cn('text-xs', theme.text.secondary)}>
                                {file.fileSize
                                  ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB`
                                  : 'Unknown size'}{' '}
                                • {file.fileType || 'Unknown type'}
                              </div>
                            </div>
                            <a
                              href={API_ENDPOINTS.fileAttachment.download(file.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={cn(
                                'text-xs px-3 py-1 rounded-md transition-colors',
                                theme.colors.primary,
                                theme.text.inverse,
                                'hover:opacity-80'
                              )}
                            >
                              Download
                            </a>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
            </div>
          ) : (
            <div className={cn('text-sm', theme.text.secondary)}>
              {labels.noSelectionText || 'No row selected'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SplitViewPanel;
